import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { CodeBracketIcon, PlusIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../../utils/PMDialog";
import { AIGenerator } from "../../../utils/AIGenerator";
import { aiGeneratorConfigs } from "../../../../config/aiGeneratorConfigs";
import ModernProductSelectorV2 from "../../../utils/Selector/ModernProductSelectorV2";
import {
  ACTIVE_HOUR_RATES_PARAMS,
  getHourRatesAsync,
} from "../../../../slices/offer-calculation-hour-rate";
import {
  isOfferSafeFormulaEngineEnabled,
  normalizeModuleResultForDisplay,
  parseMoneyAmount,
  resolveOfferCalculationFeatureFlags,
  shouldPreferServerModuleResult,
} from "../utils";
import {
  evaluateSafeFormula,
  formulaDependencies,
  SafeFormulaError,
  safeFormulaErrorI18nKey,
} from "./safe-formula-engine";
import { ModuleBuilderJsonDialog } from "./module-builder-json-dialog";
import ModuleGlobalsPanel from "./module-globals-panel";
import ModuleRulesPanel from "./module-rules-panel";
import {
  applyImportedModuleGraph,
  parseImportedModuleGraphJson,
} from "./module-builder-import-utils";
import ProductListEditor from "./product-list-editor";
import {
  authorProductListFromNode,
  writeProductListToNode,
} from "../utils/product-list";
import {
  NODE_HEIGHT,
  NODE_WIDTH,
  buildOrthogonalPath,
  correctModuleGraph,
  edgeLaneShift,
  findFreePosition,
  getConnectionPoint,
  preferredSidesForEdge,
  separateOverlappingNodes,
} from "./module-builder-layout";
import { sortNodesForInsertDisplay } from "./module-insert-order";
import { moduleGraphHoursTotal } from "./module-graph-hours";
import "./module-builder.css";

const STORAGE_PREFIX = "promang.offerCalculation.moduleGraph.";
const DEFAULT_VIEWPORT = { x: 80, y: 60, zoom: 1 };

/** Module settings / result dialogs (above default PMDialog 2000 and confirm 3600). */
const MODULE_EDITOR_Z_INDEX = 4000;
/** Nested MUI/PM dialogs opened from within the module editor (catalog, hour rate, …). */
const MODULE_EDITOR_CHILD_Z_INDEX = MODULE_EDITOR_Z_INDEX + 100;

const NODE_DEFINITIONS = {
  service: {
    labelKey: "offer.calculation.module.node.service",
    descriptionKey: "offer.calculation.module.node.service_hint",
    defaultTitle: "Leilighet",
    defaultData: { amount: 4500, enabled: true, hours: 0, hourlyRate: 0, costHourlyRate: 0, hourRateId: null },
    glyph: "S",
    advanced: false,
  },
  quantity: {
    labelKey: "offer.calculation.module.node.quantity",
    descriptionKey: "offer.calculation.module.node.quantity_hint",
    defaultTitle: "Antall kilometer",
    defaultData: { quantity: 1, unitPrice: 25 },
    glyph: "×",
    advanced: false,
  },
  productPrice: {
    labelKey: "offer.calculation.module.node.product_price",
    descriptionKey: "offer.calculation.module.node.product_price_hint",
    defaultTitle: "Produktpris",
    defaultData: { quantity: 1, unitPrice: 0, productName: "" },
    glyph: "P",
    advanced: false,
  },
  productCatalog: {
    labelKey: "offer.calculation.module.node.product_catalog",
    descriptionKey: "offer.calculation.module.node.product_catalog_hint",
    defaultTitle: "Produkter",
    defaultData: { products: [], ask_on_use: false },
    glyph: "≡",
    advanced: false,
  },
  laborHours: {
    labelKey: "offer.calculation.module.node.labor_hours",
    descriptionKey: "offer.calculation.module.node.labor_hours_hint",
    defaultTitle: "Timer",
    defaultData: { hours: 1, hourlyRate: 0, costHourlyRate: 0, hourRateId: null },
    glyph: "T",
    advanced: false,
  },
  percentage: {
    labelKey: "offer.calculation.module.node.percentage",
    descriptionKey: "offer.calculation.module.node.percentage_hint",
    defaultTitle: "Prosent",
    defaultData: { percent: 10, scope: "all", percentMode: "markup" },
    glyph: "%",
    advanced: false,
  },
  distribution: {
    labelKey: "offer.calculation.module.node.distribution",
    descriptionKey: "offer.calculation.module.node.distribution_hint",
    defaultTitle: "Fordeling på X",
    defaultData: { divisor: 2, enabled: true },
    glyph: "÷",
    advanced: false,
  },
  formula: {
    labelKey: "offer.calculation.module.node.formula",
    descriptionKey: "offer.calculation.module.node.formula_hint",
    defaultTitle: "Spesialformel",
    // Default: multiply incoming (e.g. delsummering) by this node's quantity.
    defaultData: { expression: "input * quantity", quantity: 1, price: 0, percent: 0, enabled: true },
    glyph: "ƒ",
    advanced: false,
  },
  subtotal: {
    labelKey: "offer.calculation.module.node.subtotal",
    descriptionKey: "offer.calculation.module.node.subtotal_hint",
    defaultTitle: "Delsummering",
    defaultData: {},
    glyph: "Σ",
    // Available in simple mode — common for kalkylemal chapter rollups.
    advanced: false,
  },
  total: {
    labelKey: "offer.calculation.module.node.total",
    descriptionKey: "offer.calculation.module.node.total_hint",
    defaultTitle: "Total",
    defaultData: {},
    glyph: "=",
    advanced: false,
  },
  note: {
    labelKey: "offer.calculation.module.node.note",
    descriptionKey: "offer.calculation.module.node.note_hint",
    defaultTitle: "Notat",
    defaultData: { note: "" },
    glyph: "…",
    advanced: false,
  },
};

const COMPUTED_NODE_TYPES = new Set(["percentage", "distribution", "formula", "subtotal", "total"]);

const FORMULA_VARIABLES = [
  { token: "input", labelKey: "offer.calculation.module.formula.variable.input" },
  { token: "quantity", labelKey: "offer.calculation.module.formula.variable.quantity" },
  { token: "price", labelKey: "offer.calculation.module.formula.variable.price" },
  { token: "percent", labelKey: "offer.calculation.module.formula.variable.percent" },
];

const FORMULA_FUNCTIONS = [
  { token: "SUM(input, 500)", labelKey: "offer.calculation.module.formula.function.sum" },
  { token: "ROUND(input)", labelKey: "offer.calculation.module.formula.function.round" },
  { token: "MIN(input, 10000)", labelKey: "offer.calculation.module.formula.function.min" },
  { token: "MAX(input, 2500)", labelKey: "offer.calculation.module.formula.function.max" },
  { token: "IF(input > 0, input, 0)", labelKey: "offer.calculation.module.formula.function.if" },
];

const FORMULA_TEMPLATES = [
  { expression: "input * quantity", labelKey: "offer.calculation.module.formula.template.input_times_quantity", safe: true },
  { expression: "input", labelKey: "offer.calculation.module.formula.template.same", safe: true },
  { expression: "input + 500", labelKey: "offer.calculation.module.formula.template.fixed_add", safe: true },
  { expression: "input - 500", labelKey: "offer.calculation.module.formula.template.fixed_subtract", safe: true },
  { expression: "input * 1.1", labelKey: "offer.calculation.module.formula.template.ten_percent", safe: true },
  { expression: "input * (1 + percent / 100)", labelKey: "offer.calculation.module.formula.template.custom_percent", safe: true },
  { expression: "quantity * price", labelKey: "offer.calculation.module.formula.template.quantity_price", safe: true },
  { expression: "input / 2", labelKey: "offer.calculation.module.formula.template.split_two", safe: true },
  { expression: "ROUND(input)", labelKey: "offer.calculation.module.formula.template.round", safe: true },
  { expression: "IF(input > 0, input, 0)", labelKey: "offer.calculation.module.formula.template.if", safe: true },
];

const NODE_REF_RE = /^nodes\.(.+)\.(?:amount|value)$/;

const DEFAULT_GRAPH = {
  version: 1,
  viewport: DEFAULT_VIEWPORT,
  nodes: [
    {
      id: "service-apartment",
      type: "service",
      title: "Leilighet",
      x: 80,
      y: 120,
      data: { amount: 4500, enabled: true, lockedFields: { amount: true } },
    },
    {
      id: "kilometers",
      type: "quantity",
      title: "Antall kilometer",
      x: 80,
      y: 360,
      data: { quantity: 18, unitPrice: 25, lockedFields: { unitPrice: true } },
    },
    {
      id: "job-percent",
      type: "percentage",
      title: "Påslag hele jobben",
      x: 390,
      y: 210,
      data: { percent: 12, scope: "all" },
    },
    {
      id: "distribution",
      type: "distribution",
      title: "Fordeling på X",
      x: 680,
      y: 210,
      data: { divisor: 2 },
    },
    {
      id: "total",
      type: "total",
      title: "Full service total",
      x: 950,
      y: 250,
      data: { lockedFields: {} },
    },
  ],
  edges: [
    { id: "edge-service-percent", source: "service-apartment", target: "job-percent", operation: "add" },
    { id: "edge-km-percent", source: "kilometers", target: "job-percent", operation: "add" },
    { id: "edge-percent-distribution", source: "job-percent", target: "distribution", operation: "add" },
    { id: "edge-distribution-total", source: "distribution", target: "total", operation: "add" },
  ],
};

const formatCurrency = (value) => {
  const amount = parseMoneyAmount(value);
  return new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    maximumFractionDigits: 0,
  }).format(amount != null ? amount : 0);
};

const formatCompactNumber = (value) => {
  const parsed = Number(value);
  return new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 2 }).format(
    Number.isFinite(parsed) ? parsed : 0
  );
};

const nodeMetaLabel = (node, t) => {
  const data = node?.data || {};
  switch (node?.type) {
    case "quantity":
    case "productPrice":
      return `${formatCompactNumber(data.quantity)} × ${formatCompactNumber(data.unitPrice)}`;
    case "laborHours":
      return `${formatCompactNumber(data.hours)} t × ${formatCompactNumber(data.hourlyRate)}`;
    case "percentage": {
      const mode = data.percentMode || data.percent_mode || "markup";
      const label =
        mode === "discount"
          ? t("offer.calculation.module.percent_mode.discount")
          : t("offer.calculation.module.percent_mode.markup");
      return `${label} ${t("offer.calculation.module.meta.percent", {
        percent: formatCompactNumber(data.percent),
      })}`;
    }
    case "distribution":
      return t("offer.calculation.module.meta.divisor", { divisor: formatCompactNumber(data.divisor) });
    case "service": {
      const hours = Number(data.hours) || 0;
      if (hours > 0) {
        return t("offer.calculation.module.meta.service_hours", {
          hours: formatCompactNumber(data.hours),
          rate: formatCompactNumber(data.hourlyRate),
        });
      }
      return t("offer.calculation.module.meta.fixed");
    }
    case "productCatalog":
      return t("offer.calculation.module.meta.product_lines", {
        count: (data.products || []).length,
      });
    case "formula":
      return data.expression || t("offer.calculation.module.meta.formula");
    case "subtotal":
      return t("offer.calculation.module.meta.subtotal");
    case "total":
      return t("offer.calculation.module.meta.total");
    case "note":
      return t("offer.calculation.module.meta.note");
    default:
      return "";
  }
};

const getStoredGraph = (calculationId) => {
  if (!calculationId || typeof window === "undefined") return null;

  try {
    const stored = window.localStorage.getItem(`${STORAGE_PREFIX}${calculationId}`);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn("Could not read module graph draft", error);
    return null;
  }
};

const storeGraph = (calculationId, graph) => {
  if (!calculationId || typeof window === "undefined") return;

  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${calculationId}`, JSON.stringify(graph));
  } catch (error) {
    console.warn("Could not store module graph draft", error);
  }
};

const createNode = (type, x = 120, y = 140) => {
  const definition = NODE_DEFINITIONS[type] || NODE_DEFINITIONS.service;
  const suffix = `${Date.now()}-${Math.round(Math.random() * 1000)}`;

  return {
    id: `${type}-${suffix}`,
    type,
    title: definition.defaultTitle,
    x,
    y,
    data: { ...definition.defaultData, lockedFields: {} },
  };
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const productTitle = (product) =>
  product?.displayTitle ||
  product?.title ||
  product?.name ||
  product?.productName ||
  product?.product_name ||
  "";

const normalizeProductLine = (product, index = 0) => {
  const productId = product?.product_id ?? product?.id ?? null;
  return {
    id: product?.id || productId || `manual-${Date.now()}-${index}`,
    product_id: productId,
    title: productTitle(product) || "Produkt",
    quantity: product?.quantity ?? 1,
    unitPrice: product?.unitPrice ?? product?.unit_price ?? product?.sales_price ?? 0,
    purchasePrice: product?.purchasePrice ?? product?.purchase_price ?? 0,
    included: product?.included !== false,
  };
};

const productLinesTotal = (products = []) =>
  products.reduce((sum, product) => {
    if (product?.included === false) return sum;
    return sum + toNumber(product.quantity) * toNumber(product.unitPrice);
  }, 0);

const evaluateFunction = (normalized, functionName, callback) =>
  normalized.replace(new RegExp(`\\b${functionName}\\s*\\(([^)]*)\\)`, "gi"), (_, values) => {
    const parsedValues = values.split(",").map((value) => toNumber(value.trim()));
    return callback(parsedValues).toString();
  });

/** Legacy path (flag off): SUM/ROUND/MIN/MAX + Function(). */
const evaluateExpressionLegacy = (expression, variables) => {
  const replacements = {
    input: variables.input,
    quantity: variables.quantity,
    price: variables.price,
    percent: variables.percent,
  };

  let normalized = String(expression || "0");

  Object.entries(replacements).forEach(([key, value]) => {
    normalized = normalized.replace(new RegExp(`\\b${key}\\b`, "gi"), String(toNumber(value)));
  });

  normalized = evaluateFunction(normalized, "SUM", (values) => values.reduce((sum, value) => sum + value, 0));
  normalized = evaluateFunction(normalized, "ROUND", (values) => Math.round(values[0] || 0));
  normalized = evaluateFunction(normalized, "MIN", (values) => Math.min(...values));
  normalized = evaluateFunction(normalized, "MAX", (values) => Math.max(...values));

  if (!/^[0-9+\-*/().,\s]+$/.test(normalized)) {
    return {
      value: 0,
      warning: { code: "offer.calculation.module.formula.error.unsupported_chars" },
    };
  }

  try {
    // Legacy tenants only — gated off when offer_safe_formula_engine / v2 is on.
    // eslint-disable-next-line no-new-func
    const value = Function(`"use strict"; return (${normalized});`)();
    return { value: toNumber(value), warning: null };
  } catch (error) {
    return {
      value: 0,
      warning: { code: "offer.calculation.module.formula.error.invalid" },
    };
  }
};

/**
 * Safe path (flag on): mirrors Offers::SafeFormulaEngine — no Function()/eval.
 * Preview only; persisted module_result is still sent on save for server totals.
 */
const evaluateExpressionSafe = (expression, variables) => {
  const formula = String(expression ?? "").trim() || "0";
  const flattened = {};
  Object.entries(variables || {}).forEach(([key, value]) => {
    flattened[key] = toNumber(value);
  });

  try {
    const value = evaluateSafeFormula(formula, flattened);
    return { value: toNumber(value), warning: null };
  } catch (error) {
    if (error instanceof SafeFormulaError) {
      return {
        value: 0,
        warning: {
          code: safeFormulaErrorI18nKey(error.code),
          params: error.params,
        },
      };
    }
    return {
      value: 0,
      warning: { code: "offer.calculation.module.formula.error.invalid" },
    };
  }
};

const evaluateExpression = (expression, variables, { safeMode = false } = {}) =>
  safeMode
    ? evaluateExpressionSafe(expression, variables)
    : evaluateExpressionLegacy(expression, variables);

const formatModuleWarning = (warning, t) => {
  if (!warning) return "";
  if (typeof warning === "string") return warning;
  if (warning.code) return t(warning.code, warning.params || {});
  return "";
};

export const calculateGraph = (graph, { safeMode = false } = {}) => {
  const nodes = graph?.nodes || [];
  const edges = graph?.edges || [];
  const globals = Array.isArray(graph?.globals) ? graph.globals : [];
  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const values = {};
  const warnings = [];
  const errors = [];
  const evaluationOrder = [];
  const visiting = new Set();
  const visited = new Set();

  const incomingFor = (nodeId) => edges.filter((edge) => edge.target === nodeId);

  const formulaVariables = (data, incomingTotal) => {
    const variables = {
      input: incomingTotal,
      quantity: data.quantity,
      price: data.price,
      percent: data.percent,
    };
    Object.entries(values).forEach(([id, value]) => {
      variables[`nodes.${id}.amount`] = value;
      variables[`nodes.${id}.value`] = value;
    });
    globals.forEach((global) => {
      const key = global?.key;
      if (!key) return;
      variables[`globals.${key}`] = global.value;
    });
    return variables;
  };

  const pushError = (nodeId, warning) => {
    if (!warning) return;
    const withTitle = {
      ...warning,
      params: { ...(warning.params || {}), title: nodeMap.get(nodeId)?.title || nodeId },
    };
    warnings.push(withTitle);
    errors.push({
      node_id: nodeId,
      code: withTitle.code,
      message: withTitle.code,
    });
  };

  const evaluateNode = (nodeId) => {
    if (visited.has(nodeId)) return values[nodeId] || 0;
    if (visiting.has(nodeId)) {
      pushError(nodeId, {
        code: "offer.calculation.module.formula.error.circular",
        params: { title: nodeMap.get(nodeId)?.title || nodeId },
      });
      return 0;
    }

    const node = nodeMap.get(nodeId);
    if (!node) return 0;

    visiting.add(nodeId);

    const incomingTotal = incomingFor(nodeId).reduce((sum, edge) => {
      const sourceValue = evaluateNode(edge.source);
      return edge.operation === "subtract" ? sum - sourceValue : sum + sourceValue;
    }, 0);

    let value = 0;
    const data = node.data || {};

    switch (node.type) {
      case "service":
        value = data.enabled === false ? 0 : toNumber(data.amount);
        break;
      case "quantity":
      case "productPrice":
        value = toNumber(data.quantity) * toNumber(data.unitPrice);
        break;
      case "productCatalog":
        value = productLinesTotal(data.products || []);
        break;
      case "laborHours":
        value = toNumber(data.hours) * toNumber(data.hourlyRate);
        break;
      case "percentage": {
        const amount = incomingTotal * (toNumber(data.percent) / 100);
        const mode = data.percentMode || data.percent_mode || "markup";
        value = mode === "discount" ? -amount : amount;
        break;
      }
      case "distribution":
        value = data.enabled === false || toNumber(data.divisor) <= 0 ? incomingTotal : incomingTotal / toNumber(data.divisor);
        break;
      case "formula": {
        if (data.enabled === false) {
          value = 0;
          break;
        }
        formulaDependencies(data.expression).forEach((name) => {
          const match = name.match(NODE_REF_RE);
          if (match) evaluateNode(match[1]);
        });
        const result = evaluateExpression(data.expression, formulaVariables(data, incomingTotal), { safeMode });
        value = result.value;
        if (result.warning) pushError(nodeId, result.warning);
        break;
      }
      case "subtotal":
      case "total":
        value = incomingTotal;
        break;
      default:
        value = 0;
    }

    visiting.delete(nodeId);
    visited.add(nodeId);
    evaluationOrder.push(nodeId);
    values[nodeId] = value;
    return value;
  };

  nodes.forEach((node) => evaluateNode(node.id));

  const totalNodes = nodes.filter((node) => node.type === "total");
  const total = totalNodes.length
    ? totalNodes.reduce((sum, node) => sum + toNumber(values[node.id]), 0)
    : nodes.reduce((sum, node) => sum + (edges.some((edge) => edge.source === node.id) ? 0 : toNumber(values[node.id])), 0);

  return {
    total,
    hours_total: moduleGraphHoursTotal({ nodes }),
    values,
    warnings,
    errors,
    evaluation_order: evaluationOrder,
    safe_mode: Boolean(safeMode),
    breakdown: nodes.map((node) => ({
      id: node.id,
      title: node.title,
      type: node.type,
      value: values[node.id] || 0,
    })),
    calculated_at: new Date().toISOString(),
  };
};

const calculateBezierPath = (fromPos, toPos, fromSide = "right", toSide = "left") => {
  const dist = Math.max(40, Math.abs(toPos.x - fromPos.x) / 2);
  const c1 = { x: fromPos.x, y: fromPos.y };
  const c2 = { x: toPos.x, y: toPos.y };

  if (fromSide === "right") c1.x += dist;
  else if (fromSide === "left") c1.x -= dist;
  else if (fromSide === "bottom") c1.y += dist;
  else c1.y -= dist;

  if (toSide === "left") c2.x -= dist;
  else if (toSide === "right") c2.x += dist;
  else if (toSide === "top") c2.y -= dist;
  else c2.y += dist;

  const endX = toSide === "left" ? toPos.x - 6 : toSide === "right" ? toPos.x + 6 : toPos.x;
  const endY = toSide === "top" ? toPos.y - 6 : toSide === "bottom" ? toPos.y + 6 : toPos.y;

  return `M ${fromPos.x} ${fromPos.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${endX} ${endY}`;
};

const LockGlyph = ({ locked }) => (
  <svg fill="none" height="16" viewBox="0 0 24 24" width="16" aria-hidden="true">
    {locked ? (
      <path
        d="M7 10V8a5 5 0 0 1 10 0v2M6 10h12v10H6V10Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    ) : (
      <path
        d="M7 10V8a5 5 0 0 1 9.5-2M6 10h12v10H6V10Z"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
    )}
  </svg>
);

const Field = ({
  label,
  value,
  type = "text",
  options,
  className,
  locked,
  lockedLabel = "Locked",
  unlockedLabel = "Open",
  trueLabel,
  falseLabel,
  onChange,
  onToggleLock,
}) => {
  const isBoolean = type === "boolean";
  const isEnabled = value !== false && value !== "false";

  return (
    <div className={["offer-module-builder__field", className].filter(Boolean).join(" ")}>
      <label>{label}</label>
      <div className="offer-module-builder__field-row">
        {isBoolean ? (
          <button
            className={[
              "offer-module-builder__boolean-button",
              isEnabled ? "is-active" : "is-inactive",
            ].join(" ")}
            disabled={locked}
            type="button"
            onClick={() => onChange(!isEnabled)}
          >
            {isEnabled ? trueLabel : falseLabel}
          </button>
        ) : options ? (
          <select aria-label={label} disabled={locked} value={value || ""} onChange={(event) => onChange(event.target.value)}>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === "textarea" ? (
          <textarea aria-label={label} disabled={locked} value={value || ""} onChange={(event) => onChange(event.target.value)} />
        ) : (
          <input
            aria-label={label}
            disabled={locked}
            type={type}
            value={value ?? ""}
            onChange={(event) => onChange(event.target.value)}
            onFocus={
              type === "number"
                ? (event) => {
                    event.target.dataset.calcSelectAll = "1";
                    event.target.select();
                  }
                : undefined
            }
            onMouseUp={
              type === "number"
                ? (event) => {
                    if (event.target.dataset.calcSelectAll !== "1") return;
                    event.preventDefault();
                    delete event.target.dataset.calcSelectAll;
                    event.target.select();
                  }
                : undefined
            }
          />
        )}
        {onToggleLock ? (
          <button
            className={["offer-module-builder__lock", locked ? "is-locked" : ""].join(" ")}
            type="button"
            onClick={onToggleLock}
            title={locked ? lockedLabel : unlockedLabel}
            aria-label={locked ? lockedLabel : unlockedLabel}
          >
            <LockGlyph locked={locked} />
          </button>
        ) : null}
      </div>
    </div>
  );
};

export const UserInsertField = ({ field, locked, onChange, t, value }) => {
  const isBooleanField = field.key === "enabled" || field.type === "boolean";
  const isEnabled = value !== false && value !== "false";

  if (isBooleanField) {
    return (
      <div className="offer-module-builder__field">
        <label>{field.label}</label>
        <button
          className={[
            "offer-module-builder__boolean-button",
            isEnabled ? "is-active" : "is-inactive",
          ].join(" ")}
          disabled={locked}
          type="button"
          onClick={() => onChange(!isEnabled)}
        >
          {isEnabled ? t("offer.calculation.module.enabled") : t("offer.calculation.module.disabled")}
        </button>
      </div>
    );
  }

  return (
    <Field
      label={field.label}
      locked={locked}
      onChange={onChange}
      options={field.options}
      type={field.type}
      value={value}
    />
  );
};

const ProductLinesEditor = ({
  locked,
  products = [],
  onChange,
  t,
  dialogZIndex = MODULE_EDITOR_CHILD_Z_INDEX,
}) => {
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const lines = Array.isArray(products) ? products : [];

  const updateLine = (index, patch) => {
    onChange(lines.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)));
  };

  const removeLine = (index) => {
    onChange(lines.filter((_, lineIndex) => lineIndex !== index));
  };

  const addManualLine = () => {
    onChange([...lines, normalizeProductLine({ title: t("offer.calculation.module.product.manual_title") }, lines.length)]);
  };

  const addSelectedProducts = () => {
    const nextProducts = selectedProducts.map((product, index) => normalizeProductLine(product, lines.length + index));
    onChange([...lines, ...nextProducts]);
    setSelectedProducts([]);
    setSelectorOpen(false);
  };

  return (
    <div className="offer-module-builder__product-editor">
      <div className="offer-module-builder__product-editor-header">
        <div>
          <strong>{t("offer.calculation.module.product.title")}</strong>
          <span>{t("offer.calculation.module.product.hint")}</span>
        </div>
        <div className="offer-module-builder__product-actions">
          <button className="offer-module-builder__button" disabled={locked} type="button" onClick={() => setSelectorOpen(true)}>
            {t("offer.calculation.module.product.add_from_catalog")}
          </button>
          <button className="offer-module-builder__button" disabled={locked} type="button" onClick={addManualLine}>
            {t("offer.calculation.module.product.add_manual")}
          </button>
        </div>
      </div>

      {lines.length === 0 ? (
        <div className="offer-module-builder__empty-line">{t("offer.calculation.module.product.empty")}</div>
      ) : null}

      {lines.map((line, index) => (
        <div className="offer-module-builder__product-line" key={`${line.product_id || line.id || "line"}-${index}`}>
          <input
            disabled={locked || Boolean(line.product_id)}
            placeholder={t("offer.calculation.module.field.product_name")}
            value={line.title || ""}
            onChange={(event) => updateLine(index, { title: event.target.value })}
          />
          <input
            disabled={locked}
            min="0"
            type="number"
            value={line.quantity ?? 1}
            onChange={(event) => updateLine(index, { quantity: event.target.value })}
          />
          <input
            disabled={locked}
            min="0"
            type="number"
            value={line.unitPrice ?? 0}
            onChange={(event) => updateLine(index, { unitPrice: event.target.value })}
          />
          <button
            className="offer-module-builder__line-toggle"
            disabled={locked}
            type="button"
            onClick={() => updateLine(index, { included: line.included === false })}
          >
            {line.included === false
              ? t("offer.calculation.module.product.include")
              : t("offer.calculation.module.product.exclude")}
          </button>
          <button
            className="offer-module-builder__line-remove"
            disabled={locked}
            type="button"
            onClick={() => removeLine(index)}
          >
            x
          </button>
        </div>
      ))}

      <div className="offer-module-builder__product-total">
        <span>{t("offer.calculation.module.product.total")}</span>
        <strong>{formatCurrency(productLinesTotal(lines))}</strong>
      </div>

      <PMDialog
        open={selectorOpen}
        onClose={() => setSelectorOpen(false)}
        size="6xl"
        title="offer.calculation.module.product.add_from_catalog"
        closeOnOutsideClick
        dense
        zIndex={dialogZIndex}
        panelClassName="!max-w-6xl lg:!max-w-7xl"
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-hidden">
            <ModernProductSelectorV2
              embeddedInParentDialog
              multiple
              onChange={setSelectedProducts}
              onClose={() => setSelectorOpen(false)}
              open={selectorOpen}
              selectedProducts={selectedProducts}
              showHeader
              showWarehouseOnlyDefault={false}
            />
          </div>
          <div className="flex flex-shrink-0 justify-end gap-2 border-t border-slate-200 px-1 pt-3 dark:border-slate-700">
            <button
              type="button"
              className="offer-module-builder__button"
              onClick={() => setSelectorOpen(false)}
            >
              {t("offer.calculation.module.product.cancel")}
            </button>
            <button
              type="button"
              className="offer-module-builder__button is-primary"
              disabled={!selectedProducts.length}
              onClick={addSelectedProducts}
            >
              {t("offer.calculation.module.product.add_selected")}
            </button>
          </div>
        </div>
      </PMDialog>
    </div>
  );
};

export const editableFieldsForNode = (node, t) => {
  const fields = {
    service: [
      { key: "enabled", type: "select", label: t("offer.calculation.module.field.enabled"), options: [
        { value: "true", label: t("offer.calculation.module.enabled") },
        { value: "false", label: t("offer.calculation.module.disabled") },
      ] },
      { key: "amount", type: "number", label: t("offer.calculation.module.field.amount") },
      { key: "hours", type: "number", label: t("offer.calculation.module.field.estimated_hours") },
      { key: "hourlyRate", type: "number", label: t("offer.calculation.module.field.hourly_rate") },
    ],
    quantity: [
      { key: "quantity", type: "number", label: t("offer.calculation.module.field.quantity") },
      { key: "unitPrice", type: "number", label: t("offer.calculation.module.field.unit_price") },
    ],
    productPrice: [
      { key: "productName", type: "text", label: t("offer.calculation.module.field.product_name") },
      { key: "quantity", type: "number", label: t("offer.calculation.module.field.quantity") },
      { key: "unitPrice", type: "number", label: t("offer.calculation.module.field.unit_price") },
    ],
    laborHours: [
      { key: "hours", type: "number", label: t("offer.calculation.module.field.hours") },
    ],
    percentage: [
      {
        key: "percentMode",
        type: "select",
        label: t("offer.calculation.module.field.percent_mode"),
        options: [
          { value: "markup", label: t("offer.calculation.module.percent_mode.markup") },
          { value: "discount", label: t("offer.calculation.module.percent_mode.discount") },
        ],
      },
      { key: "percent", type: "number", label: t("offer.calculation.module.field.percent") },
      { key: "scope", type: "select", label: t("offer.calculation.module.field.scope"), options: [
        { value: "all", label: t("offer.calculation.module.scope.all") },
        { value: "work", label: t("offer.calculation.module.scope.work") },
        { value: "driving", label: t("offer.calculation.module.scope.driving") },
        { value: "selected", label: t("offer.calculation.module.scope.selected") },
      ] },
    ],
    distribution: [
      { key: "enabled", type: "boolean", label: t("offer.calculation.module.field.enabled") },
      { key: "divisor", type: "number", label: t("offer.calculation.module.field.divisor") },
    ],
    formula: [
      { key: "enabled", type: "boolean", label: t("offer.calculation.module.field.enabled") },
      { key: "quantity", type: "number", label: t("offer.calculation.module.field.quantity") },
      { key: "price", type: "number", label: t("offer.calculation.module.field.unit_price") },
      { key: "percent", type: "number", label: t("offer.calculation.module.field.percent") },
    ],
  };

  return (fields[node.type] || []).filter((field) => !(node.type === "distribution" && node.data?.enabled === false && field.key === "divisor")).map((field) => ({ ...field, label: node.data?.fieldLabels?.[field.key] || field.label }));
};

const graphPreviewBounds = (graph) => {
  const padding = 160;
  const nodeWidth = NODE_WIDTH;
  const nodeHeight = NODE_HEIGHT;
  const nodes = graph.nodes || [];

  if (!nodes.length) {
    return {
      height: 560,
      minX: 0,
      minY: 0,
      width: 1180,
    };
  }

  const minX = Math.min(...nodes.map((node) => toNumber(node.x))) - padding;
  const minY = Math.min(...nodes.map((node) => toNumber(node.y))) - padding;
  const maxX = Math.max(...nodes.map((node) => toNumber(node.x) + nodeWidth)) + padding;
  const maxY = Math.max(...nodes.map((node) => toNumber(node.y) + nodeHeight)) + padding;

  return {
    height: Math.max(560, maxY - minY),
    minX,
    minY,
    width: Math.max(1180, maxX - minX),
  };
};

const GraphPreview = ({ graph, result, onSelectEdge }) => {
  const bounds = graphPreviewBounds(graph);

  return (
    <svg
      className="offer-module-builder__insert-map"
      style={{ height: bounds.height, minWidth: bounds.width, width: bounds.width }}
      viewBox={`${bounds.minX} ${bounds.minY} ${bounds.width} ${bounds.height}`}
    >
      <defs>
        <marker id="module-insert-arrow-add" markerHeight="8" markerWidth="8" orient="auto" refX="7" refY="4">
          <path d="M0,0 L8,4 L0,8 z" fill="#1463C7" />
        </marker>
        <marker id="module-insert-arrow-subtract" markerHeight="8" markerWidth="8" orient="auto" refX="7" refY="4">
          <path d="M0,0 L8,4 L0,8 z" fill="#b91c1c" />
        </marker>
      </defs>
      {graph.edges.map((edge) => {
        const source = graph.nodes.find((node) => node.id === edge.source);
        const target = graph.nodes.find((node) => node.id === edge.target);
        if (!source || !target) return null;

        const { fromSide, toSide } = preferredSidesForEdge(source, target);
        const start = getConnectionPoint(source, fromSide);
        const end = getConnectionPoint(target, toSide);
        const path = calculateBezierPath(start, end, fromSide, toSide);
        const color = edge.operation === "subtract" ? "#dc2626" : "#1463C7";
        const marker = edge.operation === "subtract" ? "url(#module-insert-arrow-subtract)" : "url(#module-insert-arrow-add)";

        return (
          <g key={edge.id}>
            <path d={path} fill="none" stroke="transparent" strokeWidth="12" onClick={() => onSelectEdge?.(edge)} />
            <path
              d={path}
              fill="none"
              markerEnd={marker}
              stroke={color}
              strokeOpacity="0.55"
              strokeWidth="1.6"
            />
          </g>
        );
      })}
      {graph.nodes.map((node) => (
        <foreignObject height={NODE_HEIGHT} key={node.id} width={NODE_WIDTH} x={node.x} y={node.y}>
          <div className="offer-module-builder__insert-node">
            <span>{node.title}</span>
            <strong>{formatCurrency(result.values[node.id])}</strong>
          </div>
        </foreignObject>
      ))}
    </svg>
  );
};

const FormulaAssistant = ({ node, data, locked, onChange, safeMode, editorMode, graph, t }) => {
  const expression = data.expression || "";
  const templates = FORMULA_TEMPLATES.filter((template) => !safeMode || template.safe !== false);
  const otherNodes = (graph?.nodes || []).filter((item) => item.id !== node.id);
  const globals = Array.isArray(graph?.globals) ? graph.globals.filter((item) => item?.key) : [];
  const appendToken = (token) => {
    const spacer = expression && !expression.endsWith(" ") ? " " : "";
    onChange(`${expression}${spacer}${token}`);
  };

  return (
    <div className="offer-module-builder__formula-assistant">
      <div className="offer-module-builder__formula-header">
        <span>{t("offer.calculation.module.formula.assistant_title")}</span>
        <span>
          {t(
            safeMode
              ? "offer.calculation.module.formula.assistant_subtitle_safe"
              : "offer.calculation.module.formula.assistant_subtitle"
          )}
        </span>
      </div>

      <div className="offer-module-builder__formula-block">
        <label>{t("offer.calculation.module.formula.templates")}</label>
        <div className="offer-module-builder__formula-grid">
          {templates.map((template) => (
            <button
              className="offer-module-builder__formula-chip"
              disabled={locked}
              key={template.expression}
              type="button"
              onClick={() => onChange(template.expression)}
            >
              {t(template.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="offer-module-builder__formula-block">
        <label>{t("offer.calculation.module.formula.variables")}</label>
        <div className="offer-module-builder__formula-grid is-compact">
          {FORMULA_VARIABLES.map((variable) => (
            <button
              className="offer-module-builder__formula-chip"
              disabled={locked}
              key={variable.token}
              type="button"
              onClick={() => appendToken(variable.token)}
            >
              {variable.token}
              <small>{t(variable.labelKey)}</small>
            </button>
          ))}
        </div>
      </div>

      {editorMode === "advanced" && otherNodes.length ? (
        <div className="offer-module-builder__formula-block">
          <label>{t("offer.calculation.module.formula.node_refs")}</label>
          <div className="offer-module-builder__formula-grid is-compact">
            {otherNodes.map((item) => {
              const token = `nodes.${item.id}.amount`;
              return (
                <button
                  className="offer-module-builder__formula-chip"
                  disabled={locked}
                  key={token}
                  type="button"
                  onClick={() => appendToken(token)}
                >
                  {token}
                  <small>{item.title}</small>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {editorMode === "advanced" && globals.length ? (
        <div className="offer-module-builder__formula-block">
          <label>{t("offer.calculation.module.formula.globals")}</label>
          <div className="offer-module-builder__formula-grid is-compact">
            {globals.map((global) => {
              const token = `globals.${global.key}`;
              return (
                <button
                  className="offer-module-builder__formula-chip"
                  disabled={locked}
                  key={token}
                  type="button"
                  onClick={() => appendToken(token)}
                >
                  {token}
                  <small>{global.label || global.key}</small>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="offer-module-builder__formula-block">
        <label>{t("offer.calculation.module.formula.functions")}</label>
        <div className="offer-module-builder__formula-grid">
          {FORMULA_FUNCTIONS.map((formulaFunction) => (
            <button
              className="offer-module-builder__formula-chip"
              disabled={locked}
              key={formulaFunction.token}
              type="button"
              onClick={() => onChange(formulaFunction.token)}
            >
              {t(formulaFunction.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div className="offer-module-builder__formula-example">
        <strong>{t("offer.calculation.module.formula.example_title")}</strong>
        <span>{t("offer.calculation.module.formula.example_text", { title: node.title })}</span>
      </div>
    </div>
  );
};

const useModuleBuilderFeatureFlags = (values) => {
  const offerFeatureFlags = useSelector((store) => store.offers?.offerFeatureFlags || {});
  const offer = useSelector((store) => store.offers?.offer || {});
  return resolveOfferCalculationFeatureFlags({
    calculation: values,
    offer,
    offerFeatureFlags,
  });
};

/**
 * Local canvas preview while editing; prefer server module_result after save/load
 * when server_evaluated (or module-builder + safe/v2 flags) and the graph is clean.
 */
const useModuleResultDisplay = ({
  values,
  setFieldValue,
  graph,
  setGraph,
  safeMode,
  featureFlags,
  persistDraft = false,
}) => {
  const calculationId = values?.id || "new";
  const [graphDirty, setGraphDirty] = useState(false);
  const lastServerStampRef = useRef(null);

  const localPreview = useMemo(() => calculateGraph(graph, { safeMode }), [graph, safeMode]);
  const preferServer = shouldPreferServerModuleResult(values?.module_result, featureFlags);
  const serverDisplay = useMemo(
    () =>
      preferServer ? normalizeModuleResultForDisplay(values?.module_result) : null,
    [preferServer, values?.module_result]
  );

  const displayResult =
    !graphDirty && serverDisplay ? serverDisplay : localPreview;

  // After save/load: adopt server graph + clear dirty when a new server_evaluated
  // stamp arrives (local preview sets server_evaluated:false and must not wipe edits).
  useEffect(() => {
    if (!values?.module_result?.server_evaluated) return;
    const stamp =
      values?.module_result?.calculated_at ||
      (values?.module_result?.server_evaluated ? "server" : null);
    const graphFromServer = values?.module_graph;
    if (!preferServer || !graphFromServer?.nodes?.length || stamp == null) return;
    if (lastServerStampRef.current === stamp) return;
    lastServerStampRef.current = stamp;
    setGraph(correctModuleGraph(graphFromServer));
    setGraphDirty(false);
  }, [preferServer, setGraph, values?.module_graph, values?.module_result]);

  useEffect(() => {
    setFieldValue("module_enabled", true);
  }, [setFieldValue]);

  useEffect(() => {
    if (!graphDirty) return;
    setFieldValue("module_graph", graph);
    setFieldValue("module_result", localPreview);
    if (persistDraft && !values?.id) {
      storeGraph(calculationId, graph);
    }
  }, [
    calculationId,
    graph,
    graphDirty,
    localPreview,
    persistDraft,
    setFieldValue,
    values?.id,
  ]);

  const markGraphDirty = () => setGraphDirty(true);

  return { displayResult, localPreview, graphDirty, markGraphDirty };
};

/**
 * Shared module canvas (toolbar, Add module, node settings, product lists).
 * Entry points must use `./module-builder-surface` so Edit calc dialog
 * (`nested-calculation-editor-dialog.js`) and the standalone page
 * (`calculation-info.js`) stay in sync — `.cursor/rules/offer-module-builder-sync.mdc`.
 */
export const OfferCalculationModuleBuilder = ({
  values,
  setFieldValue,
  fillViewport = true,
  onOpenInsert,
  onOpenHistory,
  historyEnabled = false,
  /** Base z-index for settings/result dialogs (raise when nested inside another PMDialog). */
  overlayZIndex = MODULE_EDITOR_Z_INDEX,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const dialogZIndex = overlayZIndex;
  /** Product picker / hour-rate / JSON must sit above settings PMDialog. */
  const childDialogZIndex = overlayZIndex + 200;
  const nestedSurface = overlayZIndex > MODULE_EDITOR_Z_INDEX;
  const featureFlags = useModuleBuilderFeatureFlags(values);
  const safeMode = isOfferSafeFormulaEngineEnabled(featureFlags);
  const calculationId = values?.id || "new";
  const hourRates = useSelector(
    (store) => store.offerCalculationHourRates?.hourRates || []
  );
  const hourRatesLoading = useSelector(
    (store) => store.offerCalculationHourRates?.isLoading
  );
  const [hourRatePickerNodeId, setHourRatePickerNodeId] = useState(null);

  const loadHourRates = useCallback(() => {
    return dispatch(getHourRatesAsync(ACTIVE_HOUR_RATES_PARAMS));
  }, [dispatch]);

  useEffect(() => {
    loadHourRates();
  }, [loadHourRates]);

  const openHourRatePicker = useCallback(
    (nodeId) => {
      setHourRatePickerNodeId(nodeId);
      loadHourRates();
    },
    [loadHourRates]
  );

  const initialGraph = useMemo(() => {
    if (values?.module_graph?.nodes?.length) return values.module_graph;

    return values?.id ? DEFAULT_GRAPH : getStoredGraph(calculationId) || DEFAULT_GRAPH;
  }, [calculationId, values?.id, values?.module_graph]);

  const [graph, setGraph] = useState(() => correctModuleGraph(initialGraph));
  const graphRef = useRef(graph);
  graphRef.current = graph;
  const graphHistory = useRef({ past: [], future: [], lastEdit: 0 });
  const [, redrawHistory] = useState(0);
  useEffect(() => { graphHistory.current = { past: [], future: [], lastEdit: 0 }; redrawHistory((value) => value + 1); }, [calculationId]);
  const restoreGraph = (direction) => {
    const history = graphHistory.current;
    const source = direction === "undo" ? history.past : history.future;
    const target = direction === "undo" ? history.future : history.past;
    if (!source.length) return;
    target.push(graphRef.current);
    const next = { ...source.pop(), viewport: graphRef.current.viewport };
    graphRef.current = next;
    history.lastEdit = 0;
    setGraph(next);
    markGraphDirty();
    redrawHistory((value) => value + 1);
  };
  const [selectedNodeId, setSelectedNodeId] = useState(initialGraph.nodes?.[0]?.id || null);
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [edgeContextMenu, setEdgeContextMenu] = useState(null);
  const [connectSourceId, setConnectSourceId] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [panState, setPanState] = useState(null);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [testsOpen, setTestsOpen] = useState(false);
  const [builderTab, setBuilderTab] = useState("content"); // content|questions|rules|offer_text|test
  const [testName, setTestName] = useState("");
  const [testExpected, setTestExpected] = useState("");
  const offer = useSelector((store) => store.offers?.offer || {});
  const [settingsOpen, setSettingsOpen] = useState(false);
  const workspaceRef = useRef(null);
  const rootRef = useRef(null);
  const paletteRef = useRef(null);
  const dragStateRef = useRef(null);
  const panStateRef = useRef(null);
  const didDragRef = useRef(false);
  dragStateRef.current = dragState;
  panStateRef.current = panState;

  const { displayResult: result, graphDirty, markGraphDirty } = useModuleResultDisplay({
    values,
    setFieldValue,
    graph,
    setGraph,
    safeMode,
    featureFlags,
    persistDraft: true,
  });
  const selectedNode = graph.nodes.find((node) => node.id === selectedNodeId);
  const selectedEdge = graph.edges.find((edge) => edge.id === selectedEdgeId);
  const settingsSize =
    selectedNode?.type === "productCatalog" || selectedNode?.type === "formula"
      ? "6xl"
      : nestedSurface
        ? "xl"
        : "lg";
  const settingsPanelClass = [
    "offer-module-builder__settings-panel",
    selectedNode?.type === "productCatalog" || nestedSurface
      ? "offer-module-builder__settings-panel--roomy"
      : "",
  ]
    .filter(Boolean)
    .join(" ");
  const viewport = graph.viewport || DEFAULT_VIEWPORT;

  useEffect(() => {
    if (!edgeContextMenu) return undefined;

    const closeMenu = () => setEdgeContextMenu(null);
    document.addEventListener("mousedown", closeMenu);
    document.addEventListener("contextmenu", closeMenu);

    return () => {
      document.removeEventListener("mousedown", closeMenu);
      document.removeEventListener("contextmenu", closeMenu);
    };
  }, [edgeContextMenu]);

  useEffect(() => {
    if (!paletteOpen) return undefined;
    const onPointerDown = (event) => {
      if (paletteRef.current && !paletteRef.current.contains(event.target)) {
        setPaletteOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [paletteOpen]);

  const updateGraph = (updater) => {
    const current = graphRef.current;
    const next = typeof updater === "function" ? updater(current) : updater;
    const history = graphHistory.current;
    const now = Date.now();
    if (now - history.lastEdit > 500) history.past = [...history.past.slice(-49), current];
    history.future = [];
    history.lastEdit = now;
    const normalized = { ...next, viewport: next.viewport || current.viewport || DEFAULT_VIEWPORT, version: 1 };
    graphRef.current = normalized;
    markGraphDirty();
    setGraph(normalized);
  };

  // Viewport-only changes must not flip display to local preview / overwrite server module_result.
  const updateViewport = (updater) => {
    setGraph((current) => {
      const currentViewport = current.viewport || DEFAULT_VIEWPORT;
      const nextViewport = typeof updater === "function" ? updater(currentViewport) : updater;
      return {
        ...current,
        version: 1,
        viewport: {
          x: nextViewport.x,
          y: nextViewport.y,
          zoom: clamp(nextViewport.zoom, 0.25, 2.5),
        },
      };
    });
  };

  const screenToWorld = (clientX, clientY) => {
    const rect = workspaceRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const currentViewport = graph.viewport || DEFAULT_VIEWPORT;
    return {
      x: (clientX - rect.left - currentViewport.x) / currentViewport.zoom,
      y: (clientY - rect.top - currentViewport.y) / currentViewport.zoom,
    };
  };

  const zoomCanvas = (factor, centerClientX, centerClientY) => {
    const rect = workspaceRef.current?.getBoundingClientRect();
    const centerX = centerClientX || (rect ? rect.left + rect.width / 2 : 0);
    const centerY = centerClientY || (rect ? rect.top + rect.height / 2 : 0);
    const before = screenToWorld(centerX, centerY);

    updateViewport((current) => {
      const nextZoom = clamp(current.zoom * factor, 0.25, 2.5);
      const canvasX = rect ? centerX - rect.left : 0;
      const canvasY = rect ? centerY - rect.top : 0;
      return {
        zoom: nextZoom,
        x: canvasX - before.x * nextZoom,
        y: canvasY - before.y * nextZoom,
      };
    });
  };

  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();
      event.stopPropagation();
      zoomCanvas(event.deltaY > 0 ? 0.92 : 1.08, event.clientX, event.clientY);
    };

    workspace.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      workspace.removeEventListener("wheel", handleWheel);
    };
  }, [zoomCanvas]);

  const updateNode = (nodeId, patch) => {
    updateGraph((current) => ({
      ...current,
      nodes: current.nodes.map((node) => (node.id === nodeId ? { ...node, ...patch } : node)),
    }));
  };

  const updateNodeData = (nodeId, field, value) => {
    updateGraph((current) => ({
      ...current,
      nodes: current.nodes.map((node) =>
        node.id === nodeId ? { ...node, data: { ...node.data, [field]: value } } : node
      ),
    }));
  };

  const toggleFieldLock = (nodeId, field) => {
    updateGraph((current) => ({
      ...current,
      nodes: current.nodes.map((node) => {
        if (node.id !== nodeId) return node;
        const lockedFields = { ...(node.data?.lockedFields || {}) };
        lockedFields[field] = !lockedFields[field];
        return { ...node, data: { ...node.data, lockedFields } };
      }),
    }));
  };

  const addNode = (type, x, y) => {
    const node = createNode(type, x, y);
    updateGraph((current) => {
      const pos = findFreePosition(
        x ?? node.x,
        y ?? node.y,
        current.nodes,
        node.id
      );
      return { ...current, nodes: [...current.nodes, { ...node, ...pos }] };
    });
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);
    setSettingsOpen(true);
  };

  const deleteNode = (nodeId) => {
    updateGraph((current) => ({
      ...current,
      nodes: current.nodes.filter((node) => node.id !== nodeId),
      edges: current.edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
    }));
    setSelectedNodeId(null);
    setSettingsOpen(false);
  };

  const duplicateNode = (nodeId) => {
    const source = graph.nodes.find((node) => node.id === nodeId);
    if (!source) return;
    const pos = findFreePosition(source.x + 28, source.y + 28, graph.nodes, null);
    const copy = {
      ...source,
      id: `${source.type}-${Date.now()}-${Math.round(Math.random() * 1000)}`,
      title: `${source.title} ${t("offer.calculation.module.copy_suffix")}`,
      x: pos.x,
      y: pos.y,
      data: {
        ...(source.data || {}),
        lockedFields: { ...(source.data?.lockedFields || {}) },
      },
    };
    updateGraph((current) => ({ ...current, nodes: [...current.nodes, copy] }));
    setSelectedNodeId(copy.id);
    setSelectedEdgeId(null);
  };

  const applyGraphImport = (importedGraph, mode = "replace") => {
    const next = correctModuleGraph(applyImportedModuleGraph(graph, importedGraph, mode));
    updateGraph(next);
    setSelectedNodeId(next.nodes?.[0]?.id || null);
    setSelectedEdgeId(null);
  };

  const moduleBuilderAiContext = {
    calculation_title: values?.title || "",
    customer_hint: offer?.customer?.displayTitle || offer?.customer?.name || "",
    project_hint: offer?.project?.displayTitle || offer?.project?.name || "",
    existing_node_titles: (graph.nodes || []).map((node) => node.title).filter(Boolean).join(", "),
  };

  const editorMode = graph.ui?.editor_mode === "advanced" ? "advanced" : "simple";
  const setEditorMode = (mode) => {
    setGraph((current) => ({
      ...current,
      ui: { ...(current.ui || {}), editor_mode: mode },
    }));
  };

  const paletteEntries = Object.entries(NODE_DEFINITIONS).filter(([, definition]) =>
    editorMode === "advanced" ? true : !definition.advanced
  );

  const connectToNode = (targetId) => {
    if (!connectSourceId || connectSourceId === targetId) {
      setConnectSourceId(targetId);
      return;
    }

    updateGraph((current) => {
      const exists = current.edges.some((edge) => edge.source === connectSourceId && edge.target === targetId);
      if (exists) return current;

      return {
        ...current,
        edges: [
          ...current.edges,
          {
            id: `edge-${connectSourceId}-${targetId}-${Date.now()}`,
            source: connectSourceId,
            target: targetId,
            operation: "add",
          },
        ],
      };
    });
    setConnectSourceId(null);
  };

  const updateEdge = (edgeId, patch) => {
    updateGraph((current) => ({
      ...current,
      edges: current.edges.map((edge) => (edge.id === edgeId ? { ...edge, ...patch } : edge)),
    }));
  };

  const deleteEdge = (edgeId) => {
    updateGraph((current) => ({ ...current, edges: current.edges.filter((edge) => edge.id !== edgeId) }));
    setSelectedEdgeId(null);
    setEdgeContextMenu(null);
  };

  const focusBuilderRoot = () => {
    const root = rootRef.current;
    if (root && typeof root.focus === "function") {
      root.focus({ preventScroll: true });
    }
  };

  const selectedNodeIdRef = useRef(selectedNodeId);
  const selectedEdgeIdRef = useRef(selectedEdgeId);
  const deleteNodeRef = useRef(deleteNode);
  const deleteEdgeRef = useRef(deleteEdge);
  const restoreGraphRef = useRef(restoreGraph);
  selectedNodeIdRef.current = selectedNodeId;
  selectedEdgeIdRef.current = selectedEdgeId;
  deleteNodeRef.current = deleteNode;
  deleteEdgeRef.current = deleteEdge;
  restoreGraphRef.current = restoreGraph;

  useEffect(() => {
    const isEditableTarget = (target) =>
      Boolean(target?.closest?.("input, textarea, select, [contenteditable=true]"));

    const onKeyDown = (event) => {
      const root = rootRef.current;
      if (!root) return;
      if (!root.contains(event.target) && document.activeElement !== root) return;
      if (isEditableTarget(event.target)) return;

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "z") {
        event.preventDefault();
        restoreGraphRef.current(event.shiftKey ? "redo" : "undo");
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "y") {
        event.preventDefault();
        restoreGraphRef.current("redo");
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setConnectSourceId(null);
        setSettingsOpen(false);
        setPaletteOpen(false);
        setEdgeContextMenu(null);
        return;
      }
      if (event.key === "Delete" || event.key === "Backspace") {
        const edgeId = selectedEdgeIdRef.current;
        const nodeId = selectedNodeIdRef.current;
        if (edgeId) {
          event.preventDefault();
          deleteEdgeRef.current(edgeId);
        } else if (nodeId) {
          event.preventDefault();
          deleteNodeRef.current(nodeId);
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  const openEdgeContextMenu = (edge, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedEdgeId(edge.id);
    setSelectedNodeId(null);
    setEdgeContextMenu({
      edgeId: edge.id,
      x: event.clientX,
      y: event.clientY,
    });
  };

  const onDrop = (event) => {
    event.preventDefault();
    const type = event.dataTransfer.getData("application/promang-module-node");
    if (!type || !workspaceRef.current) return;

    const point = screenToWorld(event.clientX, event.clientY);
    addNode(type, point.x - 110, point.y - 40);
  };

  useEffect(() => {
    if (!dragState && !panState) return undefined;

    const onMove = (event) => {
      const drag = dragStateRef.current;
      if (drag) {
        const zoom = drag.zoom || 1;
        const dx = (event.clientX - drag.clientX) / zoom;
        const dy = (event.clientY - drag.clientY) / zoom;
        if (Math.abs(event.clientX - drag.clientX) > 4 || Math.abs(event.clientY - drag.clientY) > 4) {
          didDragRef.current = true;
        }
        updateNode(drag.nodeId, {
          x: drag.x + dx,
          y: drag.y + dy,
        });
        return;
      }

      const pan = panStateRef.current;
      if (pan) {
        updateViewport({
          x: pan.x + event.clientX - pan.clientX,
          y: pan.y + event.clientY - pan.clientY,
          zoom: pan.zoom,
        });
      }
    };

    const onUp = () => {
      const wasNodeDrag = Boolean(dragStateRef.current);
      setDragState(null);
      setPanState(null);
      if (!wasNodeDrag) return;
      updateGraph((current) => {
        const nodes = separateOverlappingNodes(current.nodes);
        if (nodes === current.nodes) return current;
        return { ...current, nodes };
      });
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [dragState, panState]);

  const startPan = (event) => {
    if (event.button !== 0) return;
    if (event.target.closest(".offer-module-builder__node")) return;
    if (event.target.closest(".offer-module-builder__edge")) return;
    if (event.target.closest(".offer-module-builder__edge-label")) return;
    if (event.target.closest(".offer-module-builder__context-menu")) return;
    focusBuilderRoot();
    setSelectedEdgeId(null);
    setSelectedNodeId(null);
    setEdgeContextMenu(null);
    setPanState({
      clientX: event.clientX,
      clientY: event.clientY,
      x: viewport.x,
      y: viewport.y,
      zoom: viewport.zoom,
    });
  };

  const stopDrag = () => {
    setDragState(null);
    setPanState(null);
  };

  const startNodeDrag = (event, node) => {
    if (event.button !== 0) return;
    if (connectSourceId) return;
    event.preventDefault();
    event.stopPropagation();
    focusBuilderRoot();
    didDragRef.current = false;
    markGraphDirty();
    setDragState({
      nodeId: node.id,
      clientX: event.clientX,
      clientY: event.clientY,
      x: toNumber(node.x),
      y: toNumber(node.y),
      zoom: viewport.zoom,
    });
    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);
  };

  const renderNodeFields = (node) => {
    const data = node.data || {};
    const isLocked = (field) => data.lockedFields?.[field];
    const lockToggle = (field) => () => toggleFieldLock(node.id, field);
    const lockLabels = {
      lockedLabel: t("offer.calculation.module.locked"),
      unlockedLabel: t("offer.calculation.module.unlocked"),
    };

    const valueFields = (
      <>
        {node.type === "service" ? (
          <>
            <Field
              label={t("offer.calculation.module.field.amount")}
              type="number"
              value={data.amount}
              locked={isLocked("amount")}
              onChange={(value) => updateNodeData(node.id, "amount", value)}
              onToggleLock={lockToggle("amount")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.enabled")}
              type="boolean"
              value={data.enabled !== false}
              trueLabel={t("offer.calculation.module.enabled")}
              falseLabel={t("offer.calculation.module.disabled")}
              locked={isLocked("enabled")}
              onChange={(value) => updateNodeData(node.id, "enabled", value)}
              onToggleLock={lockToggle("enabled")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.estimated_hours")}
              type="number"
              value={data.hours}
              locked={isLocked("hours")}
              onChange={(value) => updateNodeData(node.id, "hours", value)}
              onToggleLock={lockToggle("hours")}
              {...lockLabels}
            />
            <div className="is-full">
              <label className="offer-module-builder__field-label">
                {t("offer.calculation.module.field.hourly_rate")}
              </label>
              <button
                type="button"
                className="offer-module-builder__button"
                disabled={isLocked("hourlyRate")}
                onClick={() => openHourRatePicker(node.id)}
              >
                {data.hourlyRate
                  ? `${formatCompactNumber(data.hourlyRate)} — ${t(
                      "offer.calculation.line_editor.select_hour_rate"
                    )}`
                  : t("offer.calculation.line_editor.select_hour_rate")}
              </button>
              <p className="offer-module-builder__hint">
                {t("offer.calculation.line_editor.hour_rate_from_catalog")}
              </p>
              <button
                className="offer-module-builder__button offer-module-builder__settings-lock"
                type="button"
                onClick={lockToggle("hourlyRate")}
              >
                {isLocked("hourlyRate")
                  ? t("offer.calculation.module.locked")
                  : t("offer.calculation.module.unlocked")}
              </button>
            </div>
            <Field
              className="is-full"
              label={t("offer.calculation.module.field.cost_hourly_rate")}
              type="number"
              value={data.costHourlyRate}
              locked
              onChange={() => {}}
              onToggleLock={lockToggle("costHourlyRate")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "quantity" || node.type === "productPrice" ? (
          <>
            {node.type === "productPrice" ? (
              <Field
                className="is-full"
                label={t("offer.calculation.module.field.product_name")}
                value={data.productName}
                locked={isLocked("productName")}
                onChange={(value) => updateNodeData(node.id, "productName", value)}
                onToggleLock={lockToggle("productName")}
                {...lockLabels}
              />
            ) : null}
            <Field
              label={t("offer.calculation.module.field.quantity")}
              type="number"
              value={data.quantity}
              locked={isLocked("quantity")}
              onChange={(value) => updateNodeData(node.id, "quantity", value)}
              onToggleLock={lockToggle("quantity")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.unit_price")}
              type="number"
              value={data.unitPrice}
              locked={isLocked("unitPrice")}
              onChange={(value) => updateNodeData(node.id, "unitPrice", value)}
              onToggleLock={lockToggle("unitPrice")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "productCatalog" ? (
          <div className="is-full">
            <ProductListEditor
              value={authorProductListFromNode(node)}
              disabled={isLocked("products")}
              dialogZIndex={childDialogZIndex}
              onChange={(nextList) => {
                const patched = writeProductListToNode(node, nextList);
                updateNode(node.id, { data: patched.data });
              }}
            />
            <button
              className="offer-module-builder__button offer-module-builder__settings-lock"
              type="button"
              onClick={lockToggle("products")}
            >
              {isLocked("products") ? t("offer.calculation.module.locked") : t("offer.calculation.module.unlocked")}
            </button>
          </div>
        ) : null}

        {node.type === "laborHours" ? (
          <>
            <Field
              label={t("offer.calculation.module.field.hours")}
              type="number"
              value={data.hours}
              locked={isLocked("hours")}
              onChange={(value) => updateNodeData(node.id, "hours", value)}
              onToggleLock={lockToggle("hours")}
              {...lockLabels}
            />
            <div className="is-full">
              <label className="offer-module-builder__field-label">
                {t("offer.calculation.module.field.hourly_rate")}
              </label>
              <button
                type="button"
                className="offer-module-builder__button"
                disabled={isLocked("hourlyRate")}
                onClick={() => openHourRatePicker(node.id)}
              >
                {data.hourlyRate
                  ? `${formatCompactNumber(data.hourlyRate)} — ${t(
                      "offer.calculation.line_editor.select_hour_rate"
                    )}`
                  : t("offer.calculation.line_editor.select_hour_rate")}
              </button>
              <p className="offer-module-builder__hint">
                {t("offer.calculation.line_editor.hour_rate_from_catalog")}
              </p>
              <button
                className="offer-module-builder__button offer-module-builder__settings-lock"
                type="button"
                onClick={lockToggle("hourlyRate")}
              >
                {isLocked("hourlyRate")
                  ? t("offer.calculation.module.locked")
                  : t("offer.calculation.module.unlocked")}
              </button>
            </div>
            <Field
              className="is-full"
              label={t("offer.calculation.module.field.cost_hourly_rate")}
              type="number"
              value={data.costHourlyRate}
              locked
              onChange={() => {}}
              onToggleLock={lockToggle("costHourlyRate")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "percentage" ? (
          <>
            <Field
              label={t("offer.calculation.module.field.percent_mode")}
              value={data.percentMode || data.percent_mode || "markup"}
              options={[
                { value: "markup", label: t("offer.calculation.module.percent_mode.markup") },
                { value: "discount", label: t("offer.calculation.module.percent_mode.discount") },
              ]}
              locked={isLocked("percentMode")}
              onChange={(value) => updateNodeData(node.id, "percentMode", value)}
              onToggleLock={lockToggle("percentMode")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.percent")}
              type="number"
              value={data.percent}
              locked={isLocked("percent")}
              onChange={(value) => updateNodeData(node.id, "percent", value)}
              onToggleLock={lockToggle("percent")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.scope")}
              value={data.scope || "all"}
              options={[
                { value: "all", label: t("offer.calculation.module.scope.all") },
                { value: "work", label: t("offer.calculation.module.scope.work") },
                { value: "driving", label: t("offer.calculation.module.scope.driving") },
                { value: "selected", label: t("offer.calculation.module.scope.selected") },
              ]}
              locked={isLocked("scope")}
              onChange={(value) => updateNodeData(node.id, "scope", value)}
              onToggleLock={lockToggle("scope")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "distribution" ? (
          <>
            <Field
              label={t("offer.calculation.module.field.enabled")}
              type="boolean"
              value={data.enabled !== false}
              trueLabel={t("offer.calculation.module.enabled")}
              falseLabel={t("offer.calculation.module.disabled")}
              locked={isLocked("enabled")}
              onChange={(value) => updateNodeData(node.id, "enabled", value)}
              onToggleLock={lockToggle("enabled")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.divisor")}
              type="number"
              value={data.divisor}
              locked={isLocked("divisor")}
              onChange={(value) => updateNodeData(node.id, "divisor", value)}
              onToggleLock={lockToggle("divisor")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "formula" ? (
          <>
            <Field
              label={t("offer.calculation.module.field.quantity")}
              type="number"
              value={data.quantity}
              locked={isLocked("quantity")}
              onChange={(value) => updateNodeData(node.id, "quantity", value)}
              onToggleLock={lockToggle("quantity")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.unit_price")}
              type="number"
              value={data.price}
              locked={isLocked("price")}
              onChange={(value) => updateNodeData(node.id, "price", value)}
              onToggleLock={lockToggle("price")}
              {...lockLabels}
            />
            <Field
              label={t("offer.calculation.module.field.percent")}
              type="number"
              value={data.percent}
              locked={isLocked("percent")}
              onChange={(value) => updateNodeData(node.id, "percent", value)}
              onToggleLock={lockToggle("percent")}
              {...lockLabels}
            />
            <div className="is-full">
              <FormulaAssistant
                data={data}
                editorMode={editorMode}
                graph={graph}
                locked={isLocked("expression")}
                node={node}
                onChange={(value) => updateNodeData(node.id, "expression", value)}
                safeMode={safeMode}
                t={t}
              />
            </div>
            <Field
              className="is-full"
              label={t("offer.calculation.module.field.expression")}
              value={data.expression}
              locked={isLocked("expression")}
              onChange={(value) => updateNodeData(node.id, "expression", value)}
              onToggleLock={lockToggle("expression")}
              {...lockLabels}
            />
          </>
        ) : null}

        {node.type === "note" ? (
          <>
            <Field
              className="is-full"
              label={t("offer.calculation.module.field.note")}
              type="textarea"
              value={data.note}
              locked={isLocked("note")}
              onChange={(value) => updateNodeData(node.id, "note", value)}
              onToggleLock={lockToggle("note")}
              {...lockLabels}
            />
            <label className="is-full flex cursor-pointer items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#1463C7] focus:ring-[#1463C7]"
                checked={data.show_in_offer !== false}
                onChange={(e) =>
                  updateNodeData(node.id, "show_in_offer", e.target.checked)
                }
              />
              <span>
                <span className="font-medium">
                  {t("offer.calculation.line_editor.show_in_offer")}
                </span>
                <span className="mt-0.5 block text-xs text-slate-500">
                  {t("offers.calculation.nested.notes_show_in_offer_hint")}
                </span>
              </span>
            </label>
          </>
        ) : null}
      </>
    );

    const hasValueFields = [
      "service",
      "quantity",
      "productPrice",
      "productCatalog",
      "laborHours",
      "percentage",
      "distribution",
      "formula",
      "note",
    ].includes(node.type);

    return (
      <div className="offer-module-builder__settings-fields">
        <section className="offer-module-builder__settings-section">
          <Field
            label={t("offer.calculation.module.field.title")}
            value={node.title}
            onChange={(value) => updateNode(node.id, { title: value })}
          />
          <Field
            className="is-full"
            label={t("offer.calculation.module.field.insert_order")}
            type="number"
            value={data.insert_order ?? ""}
            onChange={(value) => {
              if (value === "" || value == null) {
                updateNodeData(node.id, "insert_order", null);
                return;
              }
              const n = Number(value);
              updateNodeData(
                node.id,
                "insert_order",
                Number.isFinite(n) ? n : null
              );
            }}
          />
          <p className="offer-module-builder__hint is-full">
            {t("offer.calculation.module.field.insert_order_hint")}
          </p>
        </section>

        {hasValueFields ? (
          <section className="offer-module-builder__settings-section">
            <div className="offer-module-builder__section-kicker">
              {t("offer.calculation.module.settings.values")}
            </div>
            <div className="offer-module-builder__settings-grid">{valueFields}</div>
          </section>
        ) : null}
      </div>
    );
  };

  const renderPaletteItems = (afterPick) =>
    paletteEntries.map(([type, definition]) => (
      <button
        className="offer-module-builder__palette-item"
        draggable
        key={type}
        type="button"
        onClick={() => {
          addNode(type);
          afterPick?.();
        }}
        onDragStart={(event) => event.dataTransfer.setData("application/promang-module-node", type)}
      >
        <span className="offer-module-builder__palette-glyph">{definition.glyph}</span>
        <span className="offer-module-builder__palette-item-body">
          <strong>{t(definition.labelKey)}</strong>
          <span>{t(definition.descriptionKey)}</span>
        </span>
      </button>
    ));

  const renderResultBody = () => (
    <>
      <div className="offer-module-builder__hero">
        <div className="offer-module-builder__section-kicker">{t("offer.calculation.module.result_title")}</div>
        <div className="offer-module-builder__hero-total">{formatCurrency(result.total)}</div>
        <p>
          {graphDirty || !shouldPreferServerModuleResult(values?.module_result, featureFlags)
            ? t("offer.calculation.module.result_hint")
            : t("offer.calculation.module.result_hint_server")}
        </p>
      </div>

      <div className="offer-module-builder__card">
        <div className="offer-module-builder__section-kicker">{t("offer.calculation.module.breakdown_title")}</div>
        <div className="offer-module-builder__breakdown">
          {result.breakdown.map((item) => (
            <button
              className={[
                "offer-module-builder__breakdown-row",
                selectedNodeId === item.id ? "is-selected" : "",
              ].join(" ")}
              key={item.id}
              type="button"
              onClick={() => {
                setSelectedNodeId(item.id);
                setSelectedEdgeId(null);
                setResultOpen(false);
                setSettingsOpen(true);
              }}
            >
              <span>{item.title}</span>
              <strong>{formatCurrency(item.value)}</strong>
            </button>
          ))}
        </div>
        <div className="offer-module-builder__breakdown-total">
          <span>{t("offer.calculation.total")}</span>
          <strong>{formatCurrency(result.total)}</strong>
        </div>
      </div>

      {editorMode === "advanced" && (result.evaluation_order || []).length ? (
        <div className="offer-module-builder__card">
          <div className="offer-module-builder__section-kicker">
            {t("offer.calculation.module.evaluation_order")}
          </div>
          <ol className="offer-module-builder__eval-order">
            {(result.evaluation_order || []).map((nodeId) => {
              const item = result.breakdown.find((row) => row.id === nodeId);
              return (
                <li key={nodeId}>
                  <button
                    className={selectedNodeId === nodeId ? "is-selected" : ""}
                    type="button"
                    onClick={() => {
                      setSelectedNodeId(nodeId);
                      setSelectedEdgeId(null);
                      setResultOpen(false);
                      setSettingsOpen(true);
                    }}
                  >
                    <span>{item?.title || nodeId}</span>
                    <strong>{formatCurrency(item?.value)}</strong>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}

      {result.warnings.map((warning, index) => (
        <div className="offer-module-builder__warning" key={`${warning?.code || "warn"}-${index}`}>
          {formatModuleWarning(warning, t)}
        </div>
      ))}
    </>
  );

  return (
    <div
      ref={rootRef}
      className={["offer-module-builder", fillViewport ? "offer-module-builder--fill" : ""].join(" ")}
      tabIndex={0}
      role="application"
      aria-label={t("offer.calculation.module.canvas_title")}
    >
      {fillViewport ? null : (
        <aside className="offer-module-builder__panel">
          <div className="offer-module-builder__section-kicker">{t("offer.calculation.module.palette_title")}</div>
          <p className="offer-module-builder__hint">{t("offer.calculation.module.palette_hint")}</p>
          <div className="mt-3">{renderPaletteItems()}</div>
        </aside>
      )}

      <section className="offer-module-builder__canvas">
        <div
          className="offer-module-builder__tabs"
          role="tablist"
          aria-label={t("offer.calculation.module.tabs.label")}
        >
          {[
            ["content", "offer.calculation.module.tabs.content"],
            ["questions", "offer.calculation.module.tabs.questions"],
            ["rules", "offer.calculation.module.tabs.rules"],
            ["offer_text", "offer.calculation.module.tabs.offer_text"],
            ["test", "offer.calculation.module.tabs.test"],
          ].map(([id, labelKey]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={builderTab === id}
              className={
                builderTab === id
                  ? "offer-module-builder__tab is-active"
                  : "offer-module-builder__tab"
              }
              onClick={() => {
                setBuilderTab(id);
                if (id === "test") setTestsOpen(true);
              }}
            >
              {t(labelKey)}
            </button>
          ))}
        </div>

        {builderTab === "questions" ? (
          <div className="offer-module-builder__tab-panel overflow-auto p-4">
            <ModuleGlobalsPanel
              globals={graph.globals}
              onChange={(globals) =>
                updateGraph((current) => ({ ...current, globals }))
              }
            />
          </div>
        ) : null}

        {builderTab === "rules" ? (
          <div className="offer-module-builder__tab-panel overflow-auto p-4">
            <ModuleRulesPanel
              rules={graph.rules}
              globals={graph.globals}
              nodes={graph.nodes}
              onChange={(rules) =>
                updateGraph((current) => ({ ...current, rules }))
              }
            />
          </div>
        ) : null}

        {builderTab === "offer_text" ? (
          <div className="offer-module-builder__tab-panel overflow-auto p-4">
            <div className="offer-module-builder__section-kicker">
              {t("offer.calculation.module.tabs.offer_text")}
            </div>
            <p className="offer-module-builder__hint mb-2">
              {t("offer.calculation.module.offer_text.hint")}
            </p>
            <label className="mb-3 grid gap-1 text-sm">
              <span>{t("offer.calculation.module.offer_text.title_field")}</span>
              <input
                className="rounded border border-slate-300 px-2 py-1.5"
                value={values?.title || ""}
                onChange={(e) => setFieldValue?.("title", e.target.value)}
              />
            </label>
            <label className="grid gap-1 text-sm">
              <span>{t("offer.calculation.module.offer_text.description_field")}</span>
              <textarea
                className="min-h-[160px] rounded border border-slate-300 px-2 py-1.5"
                value={values?.description || ""}
                onChange={(e) => setFieldValue?.("description", e.target.value)}
              />
            </label>
            <label className="mt-3 grid gap-1 text-sm">
              <span>{t("offer.calculation.module.offer_text.customer_note")}</span>
              <textarea
                className="min-h-[100px] rounded border border-slate-300 px-2 py-1.5"
                value={graph.offer_text || ""}
                onChange={(e) =>
                  updateGraph((current) => ({
                    ...current,
                    offer_text: e.target.value,
                  }))
                }
              />
            </label>
          </div>
        ) : null}

        {builderTab === "content" || builderTab === "test" ? (
          <>
        <div className="offer-module-builder__canvas-toolbar">
          <div className="flex gap-2"><button type="button" className="offer-module-builder__button" disabled={!graphHistory.current.past.length} onClick={() => restoreGraph("undo")}>{t("offers.workspace.undo")}</button><button type="button" className="offer-module-builder__button" disabled={!graphHistory.current.future.length} onClick={() => restoreGraph("redo")}>{t("offers.workspace.redo")}</button></div>
          <div className="offer-module-builder__toolbar-lead">
            {fillViewport ? (
              <div className="offer-module-builder__palette-menu" ref={paletteRef}>
                <button
                  className="offer-module-builder__button is-primary"
                  type="button"
                  onClick={() => setPaletteOpen((open) => !open)}
                >
                  <PlusIcon className="h-4 w-4" />
                  {t("offer.calculation.module.add")}
                </button>
                {paletteOpen ? (
                  <div className="offer-module-builder__palette-popover">
                    <div className="offer-module-builder__section-kicker">
                      {t("offer.calculation.module.palette_title")}
                    </div>
                    <p className="offer-module-builder__hint">{t("offer.calculation.module.palette_hint")}</p>
                    <div className="mt-3">{renderPaletteItems(() => setPaletteOpen(false))}</div>
                  </div>
                ) : null}
              </div>
            ) : null}
            <div>
              <h3 className="offer-module-builder__section-title">{t("offer.calculation.module.canvas_title")}</h3>
              <p className="offer-module-builder__hint">
                {connectSourceId
                  ? t("offer.calculation.module.connecting_hint")
                  : t("offer.calculation.module.canvas_hint")}
              </p>
            </div>
          </div>
          <div className="offer-module-builder__toolbar-actions">
            {editorMode === "advanced" && <button className="offer-module-builder__button" type="button" onClick={() => setTestsOpen(true)}>{t("offers.workspace.template_tests")}</button>}
            <div className="offer-module-builder__mode-toggle" role="group">
              <button
                className={editorMode === "simple" ? "is-active" : ""}
                type="button"
                onClick={() => setEditorMode("simple")}
              >
                {t("offer.calculation.module.editor_mode.simple")}
              </button>
              <button
                className={editorMode === "advanced" ? "is-active" : ""}
                type="button"
                onClick={() => setEditorMode("advanced")}
              >
                {t("offer.calculation.module.editor_mode.advanced")}
              </button>
            </div>
            <div className="offer-module-builder__zoom">
              <button type="button" onClick={() => zoomCanvas(0.85)} aria-label={t("offer.calculation.module.zoom_out")}>
                −
              </button>
              <span className="offer-module-builder__zoom-label">
                {Math.round(viewport.zoom * 100)}%
              </span>
              <button type="button" onClick={() => zoomCanvas(1.15)} aria-label={t("offer.calculation.module.zoom_in")}>
                +
              </button>
            </div>
            <button className="offer-module-builder__button" type="button" onClick={() => updateViewport(DEFAULT_VIEWPORT)}>
              {t("offer.calculation.module.reset_view")}
            </button>
            {onOpenInsert ? (
              <button className="offer-module-builder__button" type="button" onClick={onOpenInsert}>
                {t("offer.calculation.builder.tab_insert")}
              </button>
            ) : null}
            {fillViewport && onOpenHistory ? (
              <button
                className="offer-module-builder__button"
                disabled={!historyEnabled}
                type="button"
                onClick={onOpenHistory}
              >
                {t("offer.calculation.builder.tab_history")}
              </button>
            ) : null}
            <button
              className="offer-module-builder__button"
              type="button"
              onClick={() => setJsonDialogOpen(true)}
            >
              <CodeBracketIcon className="h-4 w-4" />
              {t("offer.calculation.module.json.open_dialog")}
            </button>
            <AIGenerator
              module="offer_module_builder"
              config={aiGeneratorConfigs.offer_module_builder}
              context={moduleBuilderAiContext}
              fieldsToGenerate={["module_graph_json"]}
              onGenerate={(generatedFields, meta) => {
                const raw = generatedFields?.module_graph_json;
                if (raw == null || (typeof raw === "string" && !String(raw).trim())) {
                  toast.error(t("offer.calculation.module.json.ai_parse_error"));
                  return;
                }
                try {
                  const parsed = parseImportedModuleGraphJson(
                    typeof raw === "string" ? raw : JSON.stringify(raw)
                  );
                  const applyMode = meta?.generationOptions?.apply_mode || "replace";
                  applyGraphImport(parsed, applyMode === "append" ? "append" : "replace");
                  toast.success(
                    t("offer.calculation.module.json.ai_apply_success", {
                      count: parsed.nodes.length,
                    })
                  );
                } catch (err) {
                  const code = err?.code || "unknown";
                  const key = `offer.calculation.module.json.error_${code}`;
                  const msg = t(key);
                  toast.error(
                    msg !== key ? msg : t("offer.calculation.module.json.ai_parse_error")
                  );
                }
              }}
              buttonLabel={t("offer.calculation.module.json.generate_with_ai")}
              className="offer-module-builder__ai-button"
            />
            {fillViewport ? (
              <button
                className="offer-module-builder__result-chip"
                type="button"
                onClick={() => setResultOpen(true)}
              >
                <span>{t("offer.calculation.module.result_title")}</span>
                <strong>{formatCurrency(result.total)}</strong>
                {result.warnings.length ? (
                  <em>{result.warnings.length}</em>
                ) : null}
              </button>
            ) : null}
          </div>
        </div>

        <div
          className="offer-module-builder__workspace"
          onDragOver={(event) => event.preventDefault()}
          onDrop={onDrop}
          onPointerDown={startPan}
          onPointerUp={stopDrag}
          ref={workspaceRef}
          style={{
            backgroundPosition: `${viewport.x}px ${viewport.y}px`,
            backgroundSize: `${22 * viewport.zoom}px ${22 * viewport.zoom}px`,
          }}
        >
          <div
            className="offer-module-builder__world"
            style={{
              transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
            }}
          >
            <svg className="offer-module-builder__edges" overflow="visible">
              <defs>
                <filter id="module-edge-shadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="1" floodColor="#0f172a" floodOpacity="0.16" stdDeviation="1.5" />
                </filter>
                <marker id="module-arrow-add" markerHeight="10" markerWidth="10" orient="auto" refX="9" refY="5">
                  <path d="M0,0 L10,5 L0,10 z" fill="#1463C7" />
                </marker>
                <marker id="module-arrow-subtract" markerHeight="10" markerWidth="10" orient="auto" refX="9" refY="5">
                  <path d="M0,0 L10,5 L0,10 z" fill="#dc2626" />
                </marker>
              </defs>
              {graph.edges.map((edge) => {
                const source = graph.nodes.find((node) => node.id === edge.source);
                const target = graph.nodes.find((node) => node.id === edge.target);
                if (!source || !target) return null;

                const { fromSide, toSide } = preferredSidesForEdge(source, target);
                const start = getConnectionPoint(source, fromSide);
                const end = getConnectionPoint(target, toSide);
                const nodesById = Object.fromEntries(graph.nodes.map((node) => [node.id, node]));
                const path = buildOrthogonalPath({
                  from: start,
                  to: end,
                  fromSide,
                  toSide,
                  obstacles: graph.nodes.filter((node) => node.id !== source.id && node.id !== target.id),
                  laneShift: edgeLaneShift(edge, graph.edges, nodesById),
                });
                const midX = path.labelX;
                const midY = path.labelY;
                const color = edge.operation === "subtract" ? "#dc2626" : "#1463C7";
                const marker = edge.operation === "subtract" ? "url(#module-arrow-subtract)" : "url(#module-arrow-add)";
                const isSelected = selectedEdgeId === edge.id;

                return (
                  <g
                    className="offer-module-builder__edge"
                    key={edge.id}
                    onClick={(event) => {
                      event.stopPropagation();
                      setSelectedEdgeId(edge.id);
                      setSelectedNodeId(null);
                    }}
                    onDoubleClick={(event) => {
                      event.stopPropagation();
                      setSelectedEdgeId(edge.id);
                      setSelectedNodeId(null);
                      setSettingsOpen(true);
                    }}
                    onContextMenu={(event) => openEdgeContextMenu(edge, event)}
                  >
                    <path
                      d={path.d}
                      fill="none"
                      stroke="transparent"
                      strokeWidth="22"
                      style={{ cursor: "pointer", pointerEvents: "stroke" }}
                    />
                    <path
                      className="offer-module-builder__edge-visible"
                      d={path.d}
                      fill="none"
                      filter="url(#module-edge-shadow)"
                      markerEnd={marker}
                      pointerEvents="none"
                      stroke={color}
                      strokeDasharray={edge.operation === "subtract" ? "8 5" : undefined}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeOpacity={0.7}
                      strokeWidth={isSelected ? "4" : "1.8"}
                    />
                    <g className="offer-module-builder__edge-label" onContextMenu={(event) => openEdgeContextMenu(edge, event)}>
                      <rect fill="#fff" height="24" rx="12" stroke={color} width="74" x={midX - 37} y={midY - 12} />
                      <text fill={color} fontSize="12" fontWeight="700" textAnchor="middle" x={midX} y={midY + 4}>
                        {edge.operation === "subtract"
                          ? t("offer.calculation.module.operation.subtract")
                          : t("offer.calculation.module.operation.add")}
                      </text>
                    </g>
                  </g>
                );
              })}
            </svg>

            {graph.nodes.map((node) => {
              const computed = COMPUTED_NODE_TYPES.has(node.type);
              return (
                <div
                  className={[
                    "offer-module-builder__node",
                    selectedNodeId === node.id ? "is-selected" : "",
                    connectSourceId === node.id ? "is-connecting" : "",
                    dragState?.nodeId === node.id ? "is-dragging" : "",
                    computed ? "is-computed" : "",
                    node.type === "total" ? "is-total" : "",
                  ].join(" ")}
                  key={node.id}
                  role="button"
                  tabIndex={0}
                  aria-label={node.title}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedNodeId(node.id);
                      if (connectSourceId && connectSourceId !== node.id) connectToNode(node.id);
                      else setSettingsOpen(true);
                    }
                  }}
                  onClick={() => {
                    setSelectedNodeId(node.id);
                    setSelectedEdgeId(null);
                    if (connectSourceId && connectSourceId !== node.id) {
                      connectToNode(node.id);
                    }
                  }}
                  onDoubleClick={(event) => {
                    event.stopPropagation();
                    if (didDragRef.current) return;
                    setSelectedNodeId(node.id);
                    setSelectedEdgeId(null);
                    setSettingsOpen(true);
                  }}
                  onPointerDown={(event) => startNodeDrag(event, node)}
                  style={{ left: node.x, top: node.y }}
                >
                  <div className="offer-module-builder__node-header">
                    <span className="offer-module-builder__node-type">
                      {t(NODE_DEFINITIONS[node.type]?.labelKey || "offer.calculation.module.node.service")}
                    </span>
                    {computed ? (
                      <span className="offer-module-builder__node-auto">
                        {t("offer.calculation.module.auto")}
                      </span>
                    ) : null}
                  </div>
                  <div className="offer-module-builder__node-title">{node.title}</div>
                  <div className="offer-module-builder__node-foot">
                    <span className="offer-module-builder__node-meta">{nodeMetaLabel(node, t)}</span>
                    <span className="offer-module-builder__node-total">{formatCurrency(result.values[node.id])}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {edgeContextMenu ? (
            <div
              className="offer-module-builder__context-menu"
              onClick={(event) => event.stopPropagation()}
              onContextMenu={(event) => event.preventDefault()}
              style={{ left: edgeContextMenu.x, top: edgeContextMenu.y }}
            >
              <button type="button" onClick={() => deleteEdge(edgeContextMenu.edgeId)}>
                {t("offer.calculation.module.remove_edge")}
              </button>
            </div>
          ) : null}
        </div>
          </>
        ) : null}
      </section>

      {fillViewport ? null : (
        <aside className="offer-module-builder__result">{renderResultBody()}</aside>
      )}

      <PMDialog open={testsOpen} onClose={() => setTestsOpen(false)} size="lg" title="offers.workspace.template_tests" closeOnOutsideClick zIndex={childDialogZIndex}>
        <div className="offer-module-tests">
          <p className="offer-module-tests__hint">{t("offers.workspace.template_tests_hint")}</p>
          <div className="offer-module-tests__list">
            {(graph.ui?.test_cases || []).map((test, index) => {
              const tested = {
                ...graph,
                nodes: graph.nodes.map((node) => ({
                  ...node,
                  data: { ...node.data, ...(test.inputs?.[node.id] || {}) },
                })),
              };
              const evaluated = calculateGraph(tested, { safeMode });
              const passed =
                !evaluated.errors.length && Math.abs(evaluated.total - Number(test.expected)) < 0.005;
              return (
                <div
                  className={["offer-module-tests__row", passed ? "is-pass" : "is-fail"].join(" ")}
                  key={`${test.name}-${index}`}
                >
                  <span>
                    {passed ? "✓" : "!"} {test.name}
                  </span>
                  <strong>
                    {formatCurrency(evaluated.total)} / {formatCurrency(test.expected)}
                  </strong>
                </div>
              );
            })}
            {(graph.ui?.test_cases || []).length === 0 ? (
              <p className="offer-module-tests__empty">{t("offers.workspace.template_tests_empty")}</p>
            ) : null}
          </div>
          <div className="offer-module-tests__form">
            <label>
              {t("offers.workspace.test_name")}
              <input value={testName} onChange={(event) => setTestName(event.target.value)} />
            </label>
            <label>
              {t("offers.workspace.expected")}
              <input
                type="number"
                step="any"
                value={testExpected}
                onChange={(event) => setTestExpected(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="offer-module-builder__button is-primary"
              disabled={!testName.trim() || testExpected === ""}
              onClick={() => {
                const inputs = Object.fromEntries(
                  graph.nodes.map((node) => [
                    node.id,
                    Object.fromEntries(
                      editableFieldsForNode(node, t).map((field) => [field.key, node.data?.[field.key]])
                    ),
                  ])
                );
                updateGraph((current) => ({
                  ...current,
                  ui: {
                    ...current.ui,
                    test_cases: [
                      ...(current.ui?.test_cases || []),
                      { name: testName.trim(), expected: Number(testExpected), inputs },
                    ],
                  },
                }));
                setTestName("");
                setTestExpected("");
              }}
            >
              {t("offers.workspace.save_test")}
            </button>
          </div>
        </div>
      </PMDialog>

      <PMDialog
        open={fillViewport && resultOpen}
        onClose={() => setResultOpen(false)}
        size="lg"
        title="offer.calculation.module.result_title"
        closeOnOutsideClick
        zIndex={dialogZIndex}
      >
        <div className="offer-module-builder__result-dialog">{renderResultBody()}</div>
      </PMDialog>

      <PMDialog
        open={settingsOpen && Boolean(selectedNode || selectedEdge)}
        onClose={() => setSettingsOpen(false)}
        size={settingsSize}
        title={
          selectedEdge
            ? "offer.calculation.module.edge_title"
            : "offer.calculation.module.editor_title"
        }
        closeOnOutsideClick
        zIndex={dialogZIndex}
        panelClassName={settingsPanelClass}
      >
        <div className="offer-module-builder__settings-dialog">
          {selectedEdge ? (
            <>
              <div className="offer-module-builder__settings-hero">
                <div className="offer-module-builder__settings-hero-main">
                  <span className="offer-module-builder__settings-glyph">→</span>
                  <div>
                    <div className="offer-module-builder__settings-hero-title">
                      {t("offer.calculation.module.edge_title")}
                    </div>
                    <p className="offer-module-builder__settings-hero-hint">
                      {selectedEdge.operation === "subtract"
                        ? t("offer.calculation.module.operation.subtract")
                        : t("offer.calculation.module.operation.add")}
                    </p>
                  </div>
                </div>
              </div>
              <div className="offer-module-builder__settings-fields">
                <Field
                  label={t("offer.calculation.module.field.operation")}
                  value={selectedEdge.operation}
                  options={[
                    { value: "add", label: t("offer.calculation.module.operation.add") },
                    { value: "subtract", label: t("offer.calculation.module.operation.subtract") },
                  ]}
                  onChange={(value) => updateEdge(selectedEdge.id, { operation: value })}
                />
              </div>
              <div className="offer-module-builder__settings-footer">
                <div className="offer-module-builder__settings-footer-start" />
                <div className="offer-module-builder__settings-footer-end">
                  <button
                    className="offer-module-builder__button is-danger"
                    type="button"
                    onClick={() => {
                      deleteEdge(selectedEdge.id);
                      setSettingsOpen(false);
                    }}
                  >
                    {t("offer.calculation.module.remove_edge")}
                  </button>
                  <button
                    className="offer-module-builder__button is-primary"
                    type="button"
                    onClick={() => setSettingsOpen(false)}
                  >
                    {t("offer.calculation.settings.done")}
                  </button>
                </div>
              </div>
            </>
          ) : selectedNode ? (
            <>
              <div className="offer-module-builder__settings-hero">
                <div className="offer-module-builder__settings-hero-main">
                  <span className="offer-module-builder__settings-glyph">
                    {NODE_DEFINITIONS[selectedNode.type]?.glyph || "S"}
                  </span>
                  <div>
                    <div className="offer-module-builder__settings-hero-kicker">
                      {t(NODE_DEFINITIONS[selectedNode.type]?.labelKey || "offer.calculation.module.node.service")}
                    </div>
                    <div className="offer-module-builder__settings-hero-title">
                      {selectedNode.title ||
                        t(NODE_DEFINITIONS[selectedNode.type]?.labelKey || "offer.calculation.module.node.service")}
                    </div>
                    <p className="offer-module-builder__settings-hero-hint">
                      {COMPUTED_NODE_TYPES.has(selectedNode.type)
                        ? t("offer.calculation.module.editor_hint_computed")
                        : t("offer.calculation.module.editor_hint_editable")}
                    </p>
                  </div>
                </div>
                {selectedNode.type === "note" ? null : (
                  <div className="offer-module-builder__settings-hero-value">
                    <span>{t("offer.calculation.module.field.amount")}</span>
                    <strong>{formatCurrency(result.values[selectedNode.id])}</strong>
                  </div>
                )}
              </div>
              {renderNodeFields(selectedNode)}
              <div className="offer-module-builder__settings-footer">
                <div className="offer-module-builder__settings-footer-start">
                  <button
                    className="offer-module-builder__button"
                    type="button"
                    onClick={() => {
                      setSettingsOpen(false);
                      connectToNode(selectedNode.id);
                    }}
                  >
                    {connectSourceId && connectSourceId !== selectedNode.id
                      ? t("offer.calculation.module.connect_here")
                      : t("offer.calculation.module.connect")}
                  </button>
                  <button
                    className="offer-module-builder__button"
                    type="button"
                    onClick={() => duplicateNode(selectedNode.id)}
                  >
                    {t("offer.calculation.module.duplicate")}
                  </button>
                </div>
                <div className="offer-module-builder__settings-footer-end">
                  <button
                    className="offer-module-builder__button is-danger"
                    type="button"
                    onClick={() => deleteNode(selectedNode.id)}
                  >
                    {t("offer.calculation.module.remove")}
                  </button>
                  <button
                    className="offer-module-builder__button is-primary"
                    type="button"
                    onClick={() => setSettingsOpen(false)}
                  >
                    {t("offer.calculation.settings.done")}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </PMDialog>

      <ModuleBuilderJsonDialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        graph={graph}
        aiContext={moduleBuilderAiContext}
        zIndex={childDialogZIndex}
        onApply={({ graph: nextGraph, mode }) => {
          applyGraphImport(nextGraph, mode === "append" ? "append" : "replace");
        }}
      />

      <PMDialog
        open={hourRatePickerNodeId != null}
        onClose={() => setHourRatePickerNodeId(null)}
        size="sm"
        title="offer.calculation.line_editor.select_hour_rate"
        closeOnOutsideClick
        zIndex={childDialogZIndex}
      >
        <div className="mt-1 space-y-2">
          {hourRatesLoading && (!Array.isArray(hourRates) || hourRates.length === 0) ? (
            <div>{t("Loading")}…</div>
          ) : Array.isArray(hourRates) && hourRates.length > 0 ? (
            hourRates.map((hourRate) => (
              <button
                key={hourRate.id}
                type="button"
                className="offer-module-builder__button flex w-full items-center justify-between text-left"
                onClick={() => {
                  if (!hourRatePickerNodeId) return;
                  updateNodeData(
                    hourRatePickerNodeId,
                    "hourlyRate",
                    hourRate.hour_rate || 0
                  );
                  updateNodeData(
                    hourRatePickerNodeId,
                    "costHourlyRate",
                    hourRate.cost_rate || 0
                  );
                  updateNodeData(hourRatePickerNodeId, "hourRateId", hourRate.id);
                  setHourRatePickerNodeId(null);
                }}
              >
                <strong>{hourRate.title}</strong>
                <span>{formatCompactNumber(hourRate.hour_rate || 0)}</span>
              </button>
            ))
          ) : (
            <div>
              {t("offer.calculation.line_editor.no_hour_rates")}{" "}
              <a
                href="/dashboard/offers/hour-rates/new"
                target="_blank"
                rel="noreferrer"
              >
                {t("offer.calculation.line_editor.create_hour_rate")}
              </a>
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="offer-module-builder__button"
            onClick={() => setHourRatePickerNodeId(null)}
          >
            {t("common.cancel")}
          </button>
        </div>
      </PMDialog>
    </div>
  );
};


export const OfferCalculationUserInsertView = ({
  values,
  setFieldValue,
  hideTitle = false,
  overlayZIndex = MODULE_EDITOR_Z_INDEX,
}) => {
  const { t } = useTranslation();
  const featureFlags = useModuleBuilderFeatureFlags(values);
  const safeMode = isOfferSafeFormulaEngineEnabled(featureFlags);
  const calculationId = values?.id || "new";
  const [activeInsertPanel, setActiveInsertPanel] = useState("fields");
  const [graph, setGraph] = useState(() => {
    if (values?.module_graph?.nodes?.length) return values.module_graph;
    return values?.id ? DEFAULT_GRAPH : getStoredGraph(calculationId) || DEFAULT_GRAPH;
  });

  const { displayResult: result, markGraphDirty } = useModuleResultDisplay({
    values,
    setFieldValue,
    graph,
    setGraph,
    safeMode,
    featureFlags,
    persistDraft: true,
  });

  const updateNodeData = (nodeId, key, value) => {
    markGraphDirty();
    setGraph((current) => ({
      ...current,
      nodes: current.nodes.map((node) => {
        if (node.id !== nodeId) return node;
        const nextValue = key === "enabled" ? value === true || value === "true" : value;
        return { ...node, data: { ...node.data, [key]: nextValue } };
      }),
    }));
  };

  const inputNodes = sortNodesForInsertDisplay(graph.nodes, graph.edges).filter(
    (node) =>
      editableFieldsForNode(node, t).length > 0 || node.type === "productCatalog"
  );

  return (
    <div className="offer-module-builder__insert-view">
      <div className="offer-module-builder__insert-header">
        <div>
          {hideTitle ? null : <h3>{t("offer.calculation.insert.title")}</h3>}
          <p>{t("offer.calculation.insert.subtitle")}</p>
        </div>
        <div className="offer-module-builder__insert-total">
          <span>{t("offer.calculation.total")}</span>
          <strong>{formatCurrency(result.total)}</strong>
        </div>
      </div>

      <div className="offer-module-builder__insert-tabs" role="tablist">
        <button
          className={activeInsertPanel === "fields" ? "is-active" : ""}
          type="button"
          onClick={() => setActiveInsertPanel("fields")}
        >
          {t("offer.calculation.insert.fields_tab")}
        </button>
        <button
          className={activeInsertPanel === "map" ? "is-active" : ""}
          type="button"
          onClick={() => setActiveInsertPanel("map")}
        >
          {t("offer.calculation.insert.map_tab")}
        </button>
      </div>

      {activeInsertPanel === "fields" ? (
      <div className="offer-module-builder__insert-grid is-fields-only">
        <div className="offer-module-builder__insert-inputs">
          <h4>{t("offer.calculation.insert.inputs_title")}</h4>
          <p>{t("offer.calculation.insert.inputs_hint")}</p>
          {inputNodes.map((node) => {
            const fields = editableFieldsForNode(node, t);
            const lockedFields = node.data?.lockedFields || {};

            return (
              <div className="offer-module-builder__insert-card" key={node.id}>
                <div className="offer-module-builder__insert-card-header">
                  <span>{node.title}</span>
                  <strong>{formatCurrency(result.values[node.id])}</strong>
                </div>
                {fields.map((field) => (
                  <UserInsertField
                    field={field}
                    key={field.key}
                    locked={Boolean(lockedFields[field.key])}
                    value={field.key === "enabled" ? node.data?.[field.key] !== false : node.data?.[field.key] ?? ""}
                    onChange={(value) => updateNodeData(node.id, field.key, value)}
                    t={t}
                  />
                ))}
                {node.type === "productCatalog" ? (
                  <ProductLinesEditor
                    locked={Boolean(lockedFields.products)}
                    products={node.data?.products || []}
                    onChange={(products) => updateNodeData(node.id, "products", products)}
                    dialogZIndex={overlayZIndex + 200}
                    t={t}
                  />
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="offer-module-builder__insert-summary">
          <h4>{t("offer.calculation.module.breakdown_title")}</h4>
          {result.breakdown.map((item) => (
            <div className="offer-module-builder__breakdown-row" key={item.id}>
              <span>{item.title}</span>
              <strong>{formatCurrency(item.value)}</strong>
            </div>
          ))}
          {result.warnings.map((warning, index) => (
            <div className="offer-module-builder__warning" key={`${warning?.code || "warn"}-${index}`}>
              {formatModuleWarning(warning, t)}
            </div>
          ))}
        </div>
      </div>
      ) : (
        <div className="offer-module-builder__insert-map-shell is-expanded">
          <div className="offer-module-builder__insert-map-toolbar">
            <div>
              <h4>{t("offer.calculation.insert.flow_title")}</h4>
              <p>{t("offer.calculation.insert.flow_hint")}</p>
            </div>
          </div>
          <GraphPreview graph={graph} result={result} />
        </div>
      )}
    </div>
  );
};

export default OfferCalculationModuleBuilder;
